import "./index.css";
import { Fragment } from "react";
import trumpetImage from "./assets/trumpetplay.jpg";
import { jobHistory } from "./jobHistory";
import { connections, RAIL_GUTTER } from "./scaffold/connectionConfig";
import { useConnections } from "./scaffold/useConnections";
import { ConnectionLayer } from "./scaffold/ConnectionLayer";
import { Reveal } from "./scaffold/Reveal";
import { GitBranch, Phone, Mail } from "lucide-react";

// Desktop cards are boxed; on mobile the rail and branches carry the structure.
const CARD =
  "md:bg-slate-800/50 md:backdrop-blur-sm md:rounded-xl md:p-6 md:border md:border-slate-700";
const SECTION_HEADING =
  "section-heading text-xl md:text-2xl font-bold text-blue-400 mb-3 md:mb-4";

export function App() {
  const { lines, breakpoint } = useConnections(connections);
  const isMobile = breakpoint === "mobile";

  // Desktop: everything plays on load with staggered delays.
  // Mobile: each section waits for its branch line (keyed by connection id in
  // connectionConfig) to draw, then fades up. Nothing below the fold is timed.
  const sectionReveal = (key: string) =>
    isMobile
      ? { revealKey: key, delay: 0.45, from: { y: 14 } }
      : { delay: 0.2, from: { x: -20 } };
  const jobReveal = (index: number) =>
    isMobile
      ? { revealKey: `spine-to-job-${index}`, delay: 0.45, from: { y: 14 } }
      : { delay: 0.8 + index * 0.2 + 0.9, from: { x: 20 } };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white relative overflow-hidden">
      {/* Animated Connection Lines */}
      <ConnectionLayer lines={lines} />

      {/* Main Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-6 pt-8 pb-20 md:py-12">
        {/* Header Section (above the fold: always a load animation) */}
        <Reveal className="header-block mb-12" innerClassName="text-center" duration={0.8} from={{ y: -20 }}>
          <h1 className="text-4xl md:text-6xl font-bold mb-2 bg-gradient-to-r from-blue-400 to-blue-200 bg-clip-text text-transparent">
            EVAN FEENSTRA
          </h1>
          <p className="text-lg md:text-2xl text-slate-300 subtitle">
            Web Developer &amp; Internet Systems Architect
          </p>
        </Reveal>

        {/* Main Grid Layout */}
        <div className="grid md:grid-cols-[350px_1fr] gap-10 md:gap-8 mb-12">
          {/* Left Column - Photo, Contact, Education, Awards */}
          <div className="space-y-6">
            {/* Profile Image */}
            <Reveal
              className="profile-image"
              innerClassName="relative"
              duration={0.8}
              delay={0.2}
              from={isMobile ? { y: 12 } : { x: -20 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full blur-xl opacity-30"></div>
              <img
                src={trumpetImage}
                alt="Evan Feenstra playing trumpet"
                className="relative rounded-full w-40 h-40 md:w-64 md:h-64 object-cover mx-auto border-4 border-blue-400/30 shadow-2xl"
              />
            </Reveal>

            {/* Sections sit beside the left rail on mobile */}
            <div
              className="space-y-8 md:space-y-6 pt-3 md:pt-0"
              style={isMobile ? { paddingLeft: RAIL_GUTTER } : undefined}
            >
              {/* Contact Section */}
              <Reveal className="contact-section" innerClassName={CARD} {...sectionReveal("spine-to-contact")}>
                <h2 className={SECTION_HEADING}>CONTACT</h2>
                <div className="space-y-3 text-slate-300">
                  <p className="flex items-center gap-2">
                    <GitBranch className="text-blue-400 shrink-0" size={20} />
                    <a
                      href="https://github.com/Evanfeenstra"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-blue-400 transition-colors"
                    >
                      github.com/Evanfeenstra
                    </a>
                  </p>
                  <p className="flex items-center gap-2">
                    <Phone className="text-blue-400 shrink-0" size={20} />
                    <a href="tel:5304004919" className="hover:text-blue-400 transition-colors">
                      (530) 400-4919
                    </a>
                  </p>
                  <p className="flex items-center gap-2">
                    <Mail className="text-blue-400 shrink-0" size={20} />
                    <a href="mailto:evan@evan.cool" className="hover:text-blue-400 transition-colors">
                      evanfeenstra@gmail.com
                    </a>
                  </p>
                </div>
              </Reveal>

              {/* Education Section */}
              <Reveal className="education-section" innerClassName={CARD} {...sectionReveal("spine-to-education")}>
                <h2 className={SECTION_HEADING}>EDUCATION</h2>
                <div className="space-y-4 text-sm">
                  <div>
                    <h3 className="font-bold text-slate-200">AI Engineering</h3>
                    <p className="text-slate-400">
                      <a
                        href="https://www.deepatlas.ai/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline decoration-1 underline-offset-2 decoration-blue-300/50 hover:text-blue-200 hover:decoration-blue-200 transition-colors"
                      >
                        Deep Atlas
                      </a>{" "}
                      - 2025
                    </p>
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-200">
                      MS, Human Centered Design and Engineering
                    </h3>
                    <p className="text-slate-400">University of Washington - 2019</p>
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-200">
                      Web Application Development
                    </h3>
                    <p className="text-slate-400">PDX Code Guild - 2015</p>
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-200">BS, Psychology</h3>
                    <p className="text-slate-400">Pitzer College - 2010</p>
                  </div>
                </div>
              </Reveal>

              {/* Awards Section */}
              <Reveal className="awards-section" innerClassName={CARD} {...sectionReveal("spine-to-awards")}>
                <h2 className={SECTION_HEADING}>AWARDS</h2>
                <div className="space-y-4 text-sm">
                  <div>
                    <h3 className="font-bold text-slate-200">1st Place - Fognet</h3>
                    <p className="text-slate-400">
                      International hackathon put on by the IOTA Foundation.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-200">
                      1st Place - IoT and Blockchain
                    </h3>
                    <p className="text-slate-400">
                      Hackathon put on by Deutsche Telekom and T-Mobile in Seattle.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-200">
                      1st Place - Smart Cities
                    </h3>
                    <p className="text-slate-400">
                      International hackathon put on by the IOTA Foundation and Sopra
                      Steria.
                    </p>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>

          {/* Right Column - Professional Experience */}
          <div className="md:pl-12">
            <Reveal
              className="experience-heading mb-14 md:mb-8"
              inView={isMobile}
              delay={isMobile ? 0 : 0.4}
              duration={isMobile ? 0.5 : 0.8}
              from={isMobile ? { y: 10 } : { x: 20 }}
            >
              <h2 className="text-xl md:text-3xl font-bold text-blue-400 text-center md:text-left">
                PROFESSIONAL EXPERIENCE
              </h2>
            </Reveal>

            {/* Jobs sit beside the right rail on mobile */}
            <div
              className="space-y-8"
              style={isMobile ? { paddingRight: RAIL_GUTTER } : undefined}
            >
              {jobHistory.map((job, index) => (
                <Reveal
                  key={index}
                  className={`job-${index}`}
                  innerClassName="text-right md:text-left md:border-l-4 md:border-blue-400 md:pl-6"
                  {...jobReveal(index)}
                >
                  <h3 className="job-title text-lg md:text-xl font-bold text-slate-100">
                    {job.title}
                  </h3>
                  <p className="text-blue-300 mb-2">
                    {job.company.split(" + ").map((name, i) => (
                      <Fragment key={name}>
                        {i > 0 && " + "}
                        {job.links?.[name] ? (
                          <a
                            href={job.links[name]}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline decoration-1 underline-offset-2 decoration-blue-300/50 hover:text-blue-200 hover:decoration-blue-200 transition-colors"
                          >
                            {name}
                          </a>
                        ) : (
                          name
                        )}
                      </Fragment>
                    ))}{" "}
                    - {job.period}
                  </p>
                  {job.description && (
                    <p className="text-slate-300">{job.description}</p>
                  )}
                  {job.products && (
                    <ul className="text-slate-300 space-y-1 md:list-disc md:pl-5">
                      {job.products.map((product) => (
                        <li key={product.name}>
                          <span className="font-semibold text-slate-200">
                            {product.name}:
                          </span>{" "}
                          {product.description}
                        </li>
                      ))}
                    </ul>
                  )}
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
